import { MutationHookOptions, useMutation } from '@apollo/client';
import {
  GetNotificationPreferencesAndLevelQuery,
  NotificationPrefDetails,
} from '../../../../../../__generated__/loma';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
} from '../../../../hooks/Analytics';
import {
  getNotificationPreferencesAndLevelQuery,
  updateNotificationPreferencesMutation,
} from '../graphql';
import { usePageContext } from '../../../../hooks/PageContext/usePageContext';
import { useGlobalSettings } from '../../../../hooks/useGlobalSettings';
import { Channels, PreferenceLevels } from '../types';

interface NotificationPreferencesMutateOptions {
  data?: GetNotificationPreferencesAndLevelQuery;
  options?: MutationHookOptions;
}

export const useSubscribeNotificationPreferenceMutate = ({
  data: notificationPreferencesAndLevelData,
  options,
}: NotificationPreferencesMutateOptions) => {
  const { state: pageContext } = usePageContext();
  const logEvent = useAnalyticsPost();

  const { retailerLevelOptInEnabled, hipaaCompliant } = useGlobalSettings();

  const {
    experienceHost,
    embedReferrerUrl,
    requestUrl,
    trackingNumbers,
    orderNumbers,
  } = pageContext;

  const referrerUrl =
    experienceHost === 'EMBED' && embedReferrerUrl
      ? embedReferrerUrl
      : (requestUrl ?? window?.location.href);

  const trackingNumber = (trackingNumbers && trackingNumbers[0]) ?? '';
  // in the context of messaging API, orderNumber is called orderId
  const orderId = (orderNumbers && orderNumbers[0]) ?? '';

  const notificationPreferences =
    notificationPreferencesAndLevelData?.getNotificationPreferencesAndLevel ??
    undefined;

  const {
    preferenceLevel,
    modifiedDatetime = '',
    isActive,
    isGuest,
  } = notificationPreferences ?? {};

  const [mutate, { loading }] = useMutation(
    updateNotificationPreferencesMutation,
    {
      refetchQueries: [getNotificationPreferencesAndLevelQuery],
      awaitRefetchQueries: true,
      ...options,
    },
  );

  const onSubscribeMutate = (
    currentChannel: Channels,
    notificationPrefDetails: NotificationPrefDetails[],
    retailerLevelChecked = false,
    marketingOptInChecked = false,
  ) => {
    // log to catch possibly missing tracking number as observed in TRACK-144
    if (
      (preferenceLevel === 'TRACKING' || !preferenceLevel) &&
      !trackingNumber
    ) {
      logEvent({
        eventComponent: NarvarEventComponents.NTH_PACK_NOTIFICATION_CENTER,
        eventFeature: NarvarEventComponents.NTH_PACK_NOTIFICATION_CENTER,
        feature: 'Notification Center',
        orderNumber: orderId,
        pageComponent: NarvarEventComponents.NTH_PACK_NOTIFICATION_CENTER,
        pageOrderNumber: orderId,
        eventNotificationSmsValidationError: `no tracking number in tracking level sms mutation. trackingNumber in page context: ${
          trackingNumbers?.toString() || ''
        }`,
        eventType: NarvarEventTypes.ERROR,
      });
    }

    mutate({
      variables: {
        ...pageContext,
        preferenceLevel:
          (retailerLevelOptInEnabled &&
            retailerLevelChecked &&
            PreferenceLevels.RETAILER) ||
          preferenceLevel ||
          PreferenceLevels.TRACKING,
        retailerMoniker: pageContext.retailerMoniker,
        trackingNumber,
        orderId,
        modifiedDatetime,
        isActive,
        isGuest,
        referrerUrl,
        hipaaCompliant,
        channel: currentChannel,
        notificationPrefDetails,
        marketingOptInChecked,
      },
      // eslint-disable-next-line no-console
    }).catch((error) => console.log(error));
  };

  return {
    onSubscribeMutate,
    loading,
  };
};
